import { TranProtocol, UsageScenario, type UsageScenarioConfig } from './usageScenarioTypes';

export { TranProtocol, UsageScenario, type UsageScenarioConfig } from './usageScenarioTypes';

export const USAGE_SCENARIO_MAP: Record<UsageScenario, UsageScenarioConfig> = {
  [UsageScenario.HTTP]: { protocol: 'HTTP', tranProtocol: TranProtocol.TCP },
  [UsageScenario.HTTPS]: { protocol: 'HTTPS', tranProtocol: TranProtocol.TCP },
  [UsageScenario.OPENAI]: { protocol: 'HTTP', tranProtocol: TranProtocol.TCP },
  [UsageScenario.ANTHROPIC]: { protocol: 'HTTP', tranProtocol: TranProtocol.TCP },
  [UsageScenario.SSH]: { protocol: '', tranProtocol: TranProtocol.TCP },
  [UsageScenario.TCP]: { protocol: '', tranProtocol: TranProtocol.TCP },
  [UsageScenario.UDP]: { protocol: '', tranProtocol: TranProtocol.UDP },
};

/** 系统镜像、驱动、运行时、工具软件、应用制品 */
export const GENERAL_ARTIFACT_USAGE_SCENARIOS: UsageScenario[] = [
  UsageScenario.TCP,
  UsageScenario.UDP,
  UsageScenario.SSH,
  UsageScenario.HTTP,
  UsageScenario.HTTPS,
];

/** 推理框架、模型实例 */
export const INFERENCE_USAGE_SCENARIOS: UsageScenario[] = [UsageScenario.OPENAI, UsageScenario.ANTHROPIC];

export function toUsageScenarioOptions(scenarios: UsageScenario[]) {
  return scenarios.map((value) => ({ label: value, value }));
}

/** 应用实例使用场景选项 */
export const APP_INSTANCE_USAGE_SCENARIO_OPTIONS = toUsageScenarioOptions(GENERAL_ARTIFACT_USAGE_SCENARIOS);

/** 模型实例使用场景选项 */
export const MODEL_INSTANCE_USAGE_SCENARIO_OPTIONS = toUsageScenarioOptions(INFERENCE_USAGE_SCENARIOS);

/** 使用场景下拉选项（全量） */
export const USAGE_SCENARIO_OPTIONS = toUsageScenarioOptions(Object.values(UsageScenario));

/** 使用场景默认端口与路径 */
export const USAGE_SCENARIO_DEFAULTS: Partial<Record<UsageScenario, { port: number; path: string }>> = {
  [UsageScenario.OPENAI]: { port: 8000, path: '/v1' },
  [UsageScenario.ANTHROPIC]: { port: 8000, path: '/v1' },
};

/** 获取使用场景默认端口与路径 */
export function getUsageScenarioDefaults(scenario?: string): { port: number; path: string } | undefined {
  if (!scenario) return undefined;
  return USAGE_SCENARIO_DEFAULTS[scenario as UsageScenario];
}

/** 解析使用场景访问路径：优先使用配置路径，否则取 USAGE_SCENARIO_DEFAULTS */
export function resolveUsageScenarioAccessPath(usageScenario: UsageScenario, configuredPath?: string): string {
  const normalized = String(configuredPath || '').trim();
  if (normalized) {
    return normalized.startsWith('/') ? normalized : `/${normalized}`;
  }
  return getUsageScenarioDefaults(usageScenario)?.path || '/';
}

/** 是否会自动回填端口与路径的使用场景 */
export function isAutoFillUsageScenario(scenario?: string): boolean {
  return !!getUsageScenarioDefaults(scenario);
}

/**
 * 使用场景切换时的端口/路径处理规则：
 * 1. 切到 OpenAI / Anthropic → 回填对应默认端口与路径
 * 2. 从上述场景切到其他 → 清空端口与路径
 * 3. 其他场景之间切换 → 不处理端口与路径
 */
export function resolveUsageScenarioPortPathOnChange(
  previousScenario?: string,
  nextScenario?: string,
): { port?: number; path?: string } | null {
  const nextDefaults = getUsageScenarioDefaults(nextScenario);
  if (nextDefaults) {
    return nextDefaults;
  }
  if (isAutoFillUsageScenario(previousScenario)) {
    return { port: undefined, path: '' };
  }
  return null;
}

/** 解析使用场景，未设置或无效时返回 undefined */
export function resolveUsageScenario(scenario?: string): UsageScenario | undefined {
  const trimmed = String(scenario || '').trim();
  if (!trimmed) return undefined;
  if (USAGE_SCENARIO_MAP[trimmed as UsageScenario]) {
    return trimmed as UsageScenario;
  }
  return undefined;
}

/** 解析使用场景展示文案 */
export function resolveUsageScenarioLabel(scenario?: string): string {
  return resolveUsageScenario(scenario) ?? '-';
}

/** 根据使用场景获取应用层协议与传输层协议 */
export function getProtocolsFromUsageScenario(scenario: UsageScenario): UsageScenarioConfig {
  return USAGE_SCENARIO_MAP[scenario];
}
